import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import { FiberManualRecord } from '@mui/icons-material';
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { reducerPaths } from "../../app/reducerPaths";
import {
  useGetVehicleDetailsQuery,
  useLazyGetVehicleDetailsQuery,
  useLazyVehicleOfferPriceQuery,
  useVehicleOfferPriceQuery,
} from "../../toolkit/carDetails/carDetailsApi";
import { useUpdateFirmPriceMutation } from "../../toolkit/carEstimate/carEstimateApi";
import { setActiveSidebar } from "../../toolkit/sidebar/sidebarSlice";
import { CAR_DELIVERY_OPTIONS } from "../../utils/constants/carEstimate";
import { routes } from "../../utils/constants/routes";
import { SellerSidebarOptions } from "../../utils/constants/sidebar";
import {
  convertFromUTCToGivenTimezone,
  convertToUTCFormat,
  convertUtcStringFormat,
  formatDateTime,
} from "../../utils/methods/dateTime";
import { CarCarousel } from "../Dashboard/components/common/CarCarousel";
import PriceIndicator from "../Dashboard/components/common/PriceIndicator";
// import SellerSidebar from "../Dashboard/components/Sidebar";
import DashboardMainContainer from "../Dashboard/DashboardMainContainer";
import { dashboardContainer } from "../Dashboard/DashboardSx";
import { AuctionTypeCard } from "./components/AuctionTypeCard";
import CarAddedToSaleModal from "./components/CarAddedToSaleModal";
import { DateTimeSection } from "./components/DateTimeSection";
import { DeliveryOptionsSection } from "./components/DeliverOptionsSection";
import { ReservePriceInput } from "./components/ReservePriceInput";
import { auctionFormSchema } from "./components/schema";
import { getImagesHandler } from "../../utils/methods/setSellMyCarForms";
import { setAlertDialog } from "../../toolkit/alertDialog/alertDialogSlice";
import { getCssVariableHandler, getRGBAFromColor } from "../../utils/methods/getCssVariables";
import theme from "../../theme";
import { auctionTypes, buyNowAuctionTimeInMins, exportAuction, exportAuctionTimeInMins, localAuction, localAuctionTimeInMins, negotiationTimeInMins } from "../../utils/constants/auction";
import { CAR_STATUS, CAR_STATUSES } from "../../utils/constants/carData";
import CarRelistedModal from "./components/CarRelistedModal";
import dayjs from "dayjs";
import CustomCheckbox from "../../components/ui/CustomCheckbox";
import { Delete, Edit } from "@mui/icons-material";
import AuctionDateTimeEditedMessageModal from "./components/AuctionDateTimeEditedModal";
import { getCommaFormattedNumber } from "../../utils/methods/stringMethods";

const CAR_CHASER_ADDRESS =
  "CarChaser's Address: 20-4544 Dufferin St, North York M3H 5X2";

const carEstimateFormaData = (values) => {
  const auctionType = values?.localAuction ? "L" : "E";
  const formData = new FormData();

  // Basic fields
  formData.set("reserve_price", values.reservePrice);
  formData.set("auction_type", auctionType);
  formData.set("delivery_option", values.deliveryOption);
  formData.set("lat", values.lat);
  formData.set("long", values.long);
  formData.set(
    "pickup_address",
    values.deliveryOption === CAR_DELIVERY_OPTIONS.Pickup
      ? values.address
      : CAR_CHASER_ADDRESS
  );

  // Date fields - ensure they're properly formatted
  if (values.pickupDate) {
    formData.set(
      "expected_date",
      convertToUTCFormat(formatDateOnly(values.pickupDate))
    );
  }

  const biddingDate =
    auctionType === "L" ? values.localAuctionDate : values.exportAuctionDate;
  const biddingTIme =
    auctionType === "L" ? values.localAuctionTime : values.exportAuctionTime;

  if (biddingDate) {
    formData.set(
      "bidding_date",
      convertToUTCFormat(formatDateTime(biddingDate, biddingTIme))
    );
  }

  // Buy Now date-time handling
  if (auctionType === "L" && values.localAuctionBuyNow) {
    const formattedDateTime = convertToUTCFormat(
      formatDateTime(
        values.localBuyNowDate,
        values.localBuyNowTime || "00:00" // default time if not provided
      )
    );
    if (formattedDateTime) {
      formData.set("buynow_date_time", formattedDateTime);
    }
  }

  if (auctionType === "E" && values.exportAuctionBuyNow) {
    const formattedDateTime = convertToUTCFormat(
      formatDateTime(
        values.exportBuyNowDate,
        values.exportBuyNowTime || "00:00" // default time if not provided
      )
    );
    if (formattedDateTime) {
      formData.set("buynow_date_time", formattedDateTime);
    }
  }

  return formData;
};

// Helper function for date-only fields
const formatDateOnly = (dateStr) => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const areAuctionTimesInThePast = (values) => {

  const liveAuctionDateTime = carEstimateFormaData(values)?.get("bidding_date");
  const buyNowDateTime = carEstimateFormaData(values)?.get("buynow_date_time");

  const liveAuctionUtcDate = liveAuctionDateTime ? new Date(convertUtcStringFormat(liveAuctionDateTime)) : null;
  let buyNowUtcDate = buyNowDateTime ? new Date(convertUtcStringFormat(buyNowDateTime)) : null;

  if (buyNowUtcDate) {
    buyNowUtcDate.setDate(buyNowUtcDate.getDate() - 1);
  }

  const currDate = new Date();

  if (liveAuctionUtcDate && buyNowUtcDate) {
    return (liveAuctionUtcDate < currDate || buyNowUtcDate < currDate);
  } else if (liveAuctionUtcDate) {
    return (liveAuctionUtcDate < currDate);
  } else if (buyNowUtcDate) {
    return (buyNowUtcDate < currDate);
  }
};

const areTimeRangesOverlapping = (values) => {

  const auctionType = values?.localAuction ? "L" : "E";

  const liveAuctionDateTime = carEstimateFormaData(values)?.get("bidding_date");
  const buyNowDateTime = carEstimateFormaData(values)?.get("buynow_date_time");

  if (!liveAuctionDateTime || !buyNowDateTime) {
    return false;
  }

  const liveAuctionUtcDate = new Date(convertUtcStringFormat(liveAuctionDateTime));
  const buyNowUtcDate = new Date(convertUtcStringFormat(buyNowDateTime));

  if (liveAuctionUtcDate < buyNowUtcDate) {
    const liveAuctionAndNegoEndDate = new Date(new Date(liveAuctionUtcDate).setMinutes(liveAuctionUtcDate.getMinutes() + 
      (auctionType === localAuction ? localAuctionTimeInMins : exportAuctionTimeInMins) + (2*negotiationTimeInMins)));
    return !(new Date(buyNowUtcDate).setDate(buyNowUtcDate.getDate() - 1) >= liveAuctionAndNegoEndDate);
  } else {
    const buyNowAuctionAndNegoEndDate = new Date(new Date(buyNowUtcDate).setMinutes(
      buyNowUtcDate.getMinutes() + negotiationTimeInMins
    ));

    return !(liveAuctionUtcDate >= buyNowAuctionAndNegoEndDate);
  }

  // let liveAuctionStartDateTime = liveAuctionUtcDate;
  // let liveAuctionEndDateTime = new Date(convertUtcStringFormat(liveAuctionDateTime));
  // liveAuctionEndDateTime.setMinutes(
  //   liveAuctionEndDateTime.getMinutes() + auctionType === localAuction ? localAuctionTimeInMins : exportAuctionTimeInMins
  // );

  // let buyNowStartDateTime = new Date(convertUtcStringFormat(buyNowDateTime));
  // buyNowStartDateTime.setMinutes(buyNowStartDateTime.getMinutes() - buyNowAuctionTimeInMins);
  // let buyNowEndDateTime = buyNowUtcDate;

  // console.log(liveAuctionStartDateTime, liveAuctionEndDateTime, buyNowStartDateTime, buyNowEndDateTime, `areTimeRangesOverlapping`)

  // return true;
  // return !(liveAuctionEndDateTime <= buyNowStartDateTime || buyNowEndDateTime <= liveAuctionStartDateTime);
}

const ScheduledActionsItem = ({
  title,
  selectedCar,
  auction_type,
  market_type,
  setEditAuctionDateAndTime
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "1rem",
      }}
      >
      <FiberManualRecord 
        sx={{
          color: "black"
        }}
      />
      <Typography>
        {title}
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: "0.5rem",
          alignItems: "center",
        }}
      >
        <Button
          sx={{
            padding: 0,
            minWidth: "0"
          }}
          onClick={() => {
            setEditAuctionDateAndTime(editAuctionDateAndTime => {
              return {
                ...editAuctionDateAndTime,
                open: true,
                auction_type,
                market_type,
              }
            });
          }}
        >
          <Edit />
        </Button>
        {/* <Button
          sx={{
            padding: 0,
            minWidth: "0"
          }}
        >
          <Delete />
        </Button> */}
      </Box>
    </Box>
  )
};

export const CarEstimateAndAuctionForm = () => {

  const editAuctionDateAndTimeInit = React.useMemo(() => {
    return {
      open: false,
      auction_type: null,
      market_type: null,
    }
  }, []);

  const navigate = useNavigate();

  const [updatePost, { status, isSuccess, isLoading }] =
    useUpdateFirmPriceMutation();

  const auth = useSelector(store => store?.[reducerPaths.auth]);
  const { vehicle_id } = useSelector(
    (state) => state?.[reducerPaths.carEstimateSlice]
  );

  const { t } = useTranslation(`carEstimateAuction`);

  const [getVehicleDetailsApiHandler, getVehicleDetailsApiData] = useLazyGetVehicleDetailsQuery();
  const [getVehicleOfferPriceApiHandler, getVehicleOfferPriceApiData] = useLazyVehicleOfferPriceQuery();

  const [openSellMyCarAsap, setOpenSellMyCarAsap] = useState(false);
  const [openEditSuccessMessageModal, setOpenEditSuccessMessageModal] = useState(false);
  const [selectedCar, setSelectedCar] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editAuctionDateAndTime, setEditAuctionDateAndTime] = React.useState(editAuctionDateAndTimeInit);

  const formik = useFormik({
    initialValues: {
      reservePrice: "",
      localAuction: true,
      exportAuction: false,
      localAuctionTimed: true,
      localAuctionBuyNow: false,
      exportAuctionTimed: false,
      exportAuctionBuyNow: false,
      localAuctionDate: "",
      localAuctionTime: "",
      localBuyNowDate: "",
      localBuyNowTime: "",
      exportAuctionDate: "",
      exportAuctionTime: "",
      exportBuyNowDate: "",
      exportBuyNowTime: "",
      deliveryOption: CAR_DELIVERY_OPTIONS.Dropoff,
      address: "",
      pickupDate: null,
    },
    validationSchema: auctionFormSchema,
    onSubmit: async (values) => {

      // Check if the auction times are in the past...
      const inThePast = areAuctionTimesInThePast(values);

      if (inThePast) {
        dispatch(
          setAlertDialog({
            open: true,
            title: `Error!`,
            message: `The auction dates have to be in the future!`
          })
        );
        return;
      }

      // Check if live auction and buy now time ranges overlap...
      const isOverlapping = areTimeRangesOverlapping(values);

      console.log(isOverlapping, `isOverlapping`)

      if (isOverlapping) {
        dispatch(
          setAlertDialog({
            open: true,
            title: "Alert!",
            message: `Please set the live auction and buy now times such that they don't overlap`
          })
        );

        return;
      }

      const formData = carEstimateFormaData(values);
      const res = await updatePost({
        formData,
        vehicleId: vehicle_id,
      });

      if (res?.error || ("status" in res?.data && !res?.data?.status)) {
        dispatch(
          setAlertDialog({
            open: true,
            title: `Error!`,
            message: res?.error?.data?.error || res?.error?.data?.message
              || res?.data?.message || `Some error occured!`
          })
        );

        return;
      }

      if (!isEditing) {
        setOpenSellMyCarAsap(true);
      } else {
        setOpenEditSuccessMessageModal(true);
      }
    },
  });
  const dispatch = useDispatch();


  // Handlers start...
  const parsedVehicle = (selectedCar) => {
    const images = [];
    const carImages = getImagesHandler({
      data: {
        vehicle: selectedCar
      }
    });



    for (let i = 0; i < Object.entries(carImages?.images)?.length; i++) {
      for (let j = 0; j < Object.entries(carImages?.images)?.[i]?.[1]?.length; j++) {
        if (Object.entries(carImages?.images)?.[i]?.[1]?.[j]) {
          // images.push({
          //   src: Object.entries(carImages?.images)?.[i]?.[1]?.[j],
          //   label: Object.entries(carImages?.notes)?.[i]?.[1]?.[j] || ""
          // })
          images.push(Object.entries(carImages?.images)?.[i]?.[1]?.[j]);
        }
      }
    }

    console.log(images, "parsedVehicle")

    return {
      ...selectedCar,
      images
    };
  }
  // Handlers end...

  useEffect(() => {
    if (!vehicle_id) {
      navigate(`${routes.dashboardNavigation}${routes.myGarage}`);
      // activating my garage screen based on index
      // NOTE: Maintain this index value with SellerSidebar options
      dispatch(
        setActiveSidebar(SellerSidebarOptions[SellerSidebarOptions.length - 2])
      );
    }
  }, [vehicle_id, navigate]);

  React.useEffect(() => {
    if (!vehicle_id) {
      return;
    }

    (async () => {
      const res = await getVehicleDetailsApiHandler({
        vehicleId: vehicle_id
      });

      const vehicleOfferPriceRes = await getVehicleOfferPriceApiHandler({
        storedVehicleId: res?.data?.vehicle?.Vehicle_Id,
        params: {
          vin: res?.data?.vehicle?.VIN,
          mileage: res?.data?.vehicle?.Mileage,
          trim: res?.data?.vehicle?.Trim,
          year: res?.data?.vehicle?.Year,
          make: res?.data?.vehicle?.Make,
          model: res?.data?.vehicle?.Model,
        }
      });

      let vehicleOfferPriceErrorMessage = ``;

      if (vehicleOfferPriceRes?.isError || ("success" in (vehicleOfferPriceRes?.data || {}) && !vehicleOfferPriceRes?.data?.success)) {

        for (let i = 0; i < Object.entries(vehicleOfferPriceRes?.error?.data?.details)?.length; i++) {
          vehicleOfferPriceErrorMessage += Object.entries(vehicleOfferPriceRes?.error?.data?.details)?.[i]?.[1] + "\n";
        }

        dispatch(
          setAlertDialog({
            open: true,
            title: `Error!`,
            message: vehicleOfferPriceErrorMessage
          })
        );
      }

      setSelectedCar(res?.data?.vehicle);

      if (res?.data?.vehicle?.auction_type && res?.data?.vehicle?.car_label_status !== CAR_STATUSES["relist-pending"]) {
        formik.setValues(values => {

          const liveAuctionUTC = res?.data?.vehicle?.Live_Auction_DateTime ? convertUtcStringFormat(res?.data?.vehicle?.Live_Auction_DateTime) : "";
          const buyNowUTC = res?.data?.vehicle?.Buy_now_DateTime ? convertUtcStringFormat(res?.data?.vehicle?.Buy_now_DateTime) : "";

          const auctionDateObj = new Date(liveAuctionUTC);
          const buyNowDateObj = new Date(buyNowUTC);

          let auctionDate = liveAuctionUTC ? auctionDateObj : "";
          let buyNowDate = buyNowUTC ? buyNowDateObj : "";

          if (auctionDate) {
            auctionDate.setHours(0);
            auctionDate.setMinutes(0);
            auctionDate.setSeconds(0);
          }

          if (buyNowDate) {
            buyNowDate.setHours(0);
            buyNowDate.setMinutes(0);
            buyNowDate.setSeconds(0);
          }

          // let localAuctionDateTime = (res?.data?.vehicle?.auction_type === localAuction ? 
          //   liveAuctionUTC ? new Date(liveAuctionUTC) : 
          //   buyNowUTC ? buyNowDateObj : "" : "");

          // if (localAuctionDateTime) {
          //   localAuctionDateTime.setHours(0);
          //   localAuctionDateTime.setMinutes(0);
          //   localAuctionDateTime.setSeconds(0);
          // }

          // let exportAuctionDateTime = (res?.data?.vehicle?.auction_type === exportAuction ? 
          //   liveAuctionUTC ? new Date(liveAuctionUTC) : 
          //   buyNowUTC ? buyNowDateObj : "" : "");

          // if (exportAuctionDateTime) {
          //   exportAuctionDateTime.setHours(0);
          //   exportAuctionDateTime.setMinutes(0);
          //   exportAuctionDateTime.setSeconds(0);
          // }

          return {
            ...values,
            reservePrice: res?.data?.vehicle?.Reserve_Price || "",
            localAuction: res?.data?.vehicle?.auction_type === localAuction,
            exportAuction: res?.data?.vehicle?.auction_type === exportAuction,
            deliveryOption: res?.data?.vehicle?.Delivery_Mode || "",
            localAuctionTimed: res?.data?.vehicle?.auction_type === localAuction,
            localAuctionDate: res?.data?.vehicle?.auction_type === localAuction && res?.data?.vehicle?.Live_Auction_DateTime ? auctionDate : formik?.values?.localAuctionDate,
            localAuctionTime: res?.data?.vehicle?.auction_type === localAuction && res?.data?.vehicle?.Live_Auction_DateTime ? 
              liveAuctionUTC ? new Date(liveAuctionUTC) : formik?.values?.localAuctionTime : formik?.values?.localAuctionTime,
            localBuyNowDate: res?.data?.vehicle?.auction_type === localAuction && res?.data?.vehicle?.Buy_now_DateTime ? buyNowDate : formik?.values?.localAuctionDate,
            localBuyNowTime: res?.data?.vehicle?.auction_type === localAuction && res?.data?.vehicle?.Buy_now_DateTime ? 
              buyNowUTC ? new Date(buyNowUTC) : formik?.values?.localBuyNowTime : formik?.values?.localBuyNowTime,
            exportAuctionDate: res?.data?.vehicle?.auction_type === exportAuction && res?.data?.vehicle?.Live_Auction_DateTime ? auctionDate : formik?.values?.exportAuctionDate,
            exportAuctionTime: res?.data?.vehicle?.auction_type === exportAuction && res?.data?.vehicle?.Live_Auction_DateTime ? 
              liveAuctionUTC ? new Date(liveAuctionUTC) : formik?.values?.exportAuctionTime : formik?.values?.exportAuctionTime,
            exportBuyNowDate: res?.data?.vehicle?.auction_type === exportAuction && res?.data?.vehicle?.Buy_now_DateTime ? buyNowDate : formik?.values?.exportBuyNowDate,
            exportBuyNowTime: res?.data?.vehicle?.auction_type === exportAuction && res?.data?.vehicle?.Buy_now_DateTime ? 
              buyNowUTC ? new Date(buyNowUTC) : formik?.values?.exportBuyNowTime : formik?.values?.exportBuyNowTime,
            localAuctionBuyNow: Boolean((res?.data?.vehicle?.auction_type === localAuction) && buyNowUTC),
            exportAuctionTimed: res?.data?.vehicle?.auction_type === exportAuction,
            exportAuctionBuyNow: Boolean((res?.data?.vehicle?.auction_type === exportAuction) && buyNowUTC),
            pickupDate: res?.data?.vehicle?.Delivery_Date || ""
          }
        });
      }
    })();
  }, [vehicle_id]);

  React.useEffect(() => {
    if (selectedCar?.Live_Auction_DateTime || selectedCar?.Buy_now_DateTime) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
  }, [
    selectedCar?.Live_Auction_DateTime,
    selectedCar?.Buy_now_DateTime
  ]);

  console.log(formik, selectedCar, "CarEstimateAndAuctionForm")

  return (
    <Box sx={dashboardContainer}>
      {/* <SellerSidebar /> */}
      {/* <DashboardMainContainer> */}
        <Paper elevation={0} sx={{ 
          py: 4, px: 6,
          width: "100%"
        }}>
          <form onSubmit={formik.handleSubmit}>
            <CarCarousel 
              images={parsedVehicle(getVehicleDetailsApiData?.data?.vehicle)?.images || []} 
              Vehicle_Id={getVehicleDetailsApiData?.data?.vehicle?.Vehicle_Id}
              Vehicle_video={getVehicleDetailsApiData?.data?.vehicle?.Vehicle_video}
              vehicleData={{
                ...getImagesHandler({ 
                  data: {
                    vehicle: getVehicleDetailsApiData?.data?.vehicle 
                  }}),
                ...getVehicleDetailsApiData?.data?.vehicle
              }}
              dontShowVehicleTitle={true}
            />

            <Box>
              {selectedCar?.auction_type && (selectedCar?.Live_Auction_DateTime || selectedCar?.Buy_now_DateTime) ? <Box
                sx={{
                  position: 'relative',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(253, 253, 253, 0.05)' : '#fafafa',
                  border: '1px solid #f0f0f0',
                  padding: "1rem",
                  mb: 2,
                  borderRadius: '8px',
                }}
              >
                <Typography 
                  color={theme.palette.primary.red}
                  fontWeight={600}
                  fontSize={"2rem"}
                  gutterBottom
                  display={"flex"}
                  gap={2}
                  alignItems={"center"}
                >
                  {t(`Scheduled auctions`)}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "1.25rem",
                  }}
                >
                    {selectedCar?.auction_type === localAuction && selectedCar?.Live_Auction_DateTime ? (
                      <ScheduledActionsItem 
                        title={`Local market live auction starts at: ${dayjs(convertUtcStringFormat(selectedCar?.Live_Auction_DateTime)).format("MMMM D, YYYY h:mm A")}`}
                        selectedCar={selectedCar}
                        setEditAuctionDateAndTime={setEditAuctionDateAndTime}
                        auction_type={auctionTypes.Live_Auction_DateTime}
                        market_type={localAuction}
                      />
                    ) : null}

                  {selectedCar?.auction_type === localAuction && selectedCar?.Buy_now_DateTime ? (
                    <ScheduledActionsItem 
                      title={`Local market buy now ends at: ${dayjs(convertUtcStringFormat(selectedCar?.Buy_now_DateTime)).format("MMMM D, YYYY h:mm A")}`}
                      selectedCar={selectedCar}
                      setEditAuctionDateAndTime={setEditAuctionDateAndTime}
                      auction_type={auctionTypes.Buy_now_DateTime}
                      market_type={localAuction}
                    />
                  ) : null}


                  {selectedCar?.auction_type === exportAuction && selectedCar?.Live_Auction_DateTime ? (
                    <ScheduledActionsItem 
                      title={`Export market live auction starts at: ${dayjs(convertUtcStringFormat(selectedCar?.Live_Auction_DateTime)).format("MMMM D, YYYY h:mm A")}`}
                      selectedCar={selectedCar}
                      setEditAuctionDateAndTime={setEditAuctionDateAndTime}
                      auction_type={auctionTypes.Live_Auction_DateTime}
                      market_type={exportAuction}
                    />
                  ) : null}

                    {selectedCar?.auction_type === exportAuction && selectedCar?.Buy_now_DateTime ? (
                      <ScheduledActionsItem 
                        title={`Export market buy now ends at: ${dayjs(convertUtcStringFormat(selectedCar?.Buy_now_DateTime)).format("MMMM D, YYYY h:mm A")}`}
                        selectedCar={selectedCar}
                        setEditAuctionDateAndTime={setEditAuctionDateAndTime}
                        auction_type={auctionTypes.Buy_now_DateTime}
                        market_type={exportAuction}
                      />
                    ) : null}
                </Box>
              </Box> : null}
            </Box>

            <Box mb={3}>
              <Typography
                color={"text.highlight"}
                fontWeight={600}
                fontSize={"2rem"}
                gutterBottom
                display={"flex"}
                gap={2}
                alignItems={"center"}
              >
                <Typography variant="h6" fontWeight="bold"
                  sx={{
                    fontSize: "1.75rem",
                  }}
                >
                  {getVehicleDetailsApiData?.data?.vehicle?.Year} {getVehicleDetailsApiData?.data?.vehicle?.Make} {getVehicleDetailsApiData?.data?.vehicle?.Model}
                </Typography>
                <Typography variant="body2" color={theme.palette.text.primary}
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: "500"
                  }}
                >
                  {`${getCommaFormattedNumber(getVehicleDetailsApiData?.data?.vehicle?.Mileage)} ${getVehicleDetailsApiData?.data?.vehicle?.Mileage_Unit} | ${getVehicleDetailsApiData?.data?.vehicle?.auction_type ? getVehicleDetailsApiData?.data?.vehicle?.auction_type === localAuction ? `Local market` : `Export market` + " | " : ""}${getVehicleDetailsApiData?.data?.vehicle?.Live_Auction_DateTime && getVehicleDetailsApiData?.data?.vehicle?.Live_Auction_DateTime ? "Live Auction" + " | " : getVehicleDetailsApiData?.data?.vehicle?.Buy_now_DateTime ? "Buy Now" + " | " : ""}${getVehicleDetailsApiData?.data?.vehicle?.VIN}`}
                  {/* 81,225 km | AWD | LE | 7MUCAABGXPV075585 */}
                </Typography>
                {/* {getVehicleDetailsApiData?.data?.vehicle?.Year} {getVehicleDetailsApiData?.data?.vehicle?.Make}
                <Typography component="span" color="text.disabled">
                  &#x2022; {getVehicleDetailsApiData?.data?.vehicle?.Model || "N/A"}
                </Typography> */}
              </Typography>

              <Typography fontWeight={600} gutterBottom>
                {t(`Car Chaser's Estimated Value Range For Your Car.`)}
              </Typography>
              <Typography color="text.form">
                {t(`Our estimated range provides a preliminary evaluation by leveraging competition within our dealer network. This estimate serves as a guide for determining your car's price.`)}
              </Typography>
            </Box>

            <Grid container spacing={12} sx={{ mb: 3 }}>
              <Grid item size={{ lg: 6, xs: 12 }}>
                <PriceIndicator
                  lowPrice={getVehicleOfferPriceApiData?.data?.second_price}
                  averagePrice={getVehicleOfferPriceApiData?.data?.third_price}
                  awesomePrice={getVehicleOfferPriceApiData?.data?.first_price}
                />
              </Grid>
              <Grid item size={{ lg: 6, xs: 12 }}>
                <ReservePriceInput formik={formik} />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <AuctionTypeCard
                type="local"
                title={t("auctionTypes.Local Auction (Buyer From Around Canada).title")}
                tooltipText={t("auctionTypes.Local Auction (Buyer From Around Canada).title")}
                timedLabel={t("auctionTypes.Local Auction (Buyer From Around Canada).95 Mins Auction")}
                buyNowLabel={t("auctionTypes.Local Auction (Buyer From Around Canada).Buy Now")}
                formik={formik}
                disabled={formik.values.exportAuction}
              />
              <AuctionTypeCard
                type="export"
                title={t("auctionTypes.Export Auction (Buyer From Around The Globe).title")}
                tooltipText={t("auctionTypes.Export Auction (Buyer From Around The Globe).title")}
                timedLabel={t("auctionTypes.Export Auction (Buyer From Around The Globe).60 Mins Auction")}
                buyNowLabel={t("auctionTypes.Export Auction (Buyer From Around The Globe).Buy Now")}
                formik={formik}
                disabled={formik.values.localAuction}
              />
            </Grid>

            {formik.values.localAuction && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {formik.values.localAuctionTimed && (
                  <Grid
                    item
                    size={{ xs: 12, lg: 6 }}
                    border={"2px dashed #E0E0E0"}
                    borderColor={"border.main"}
                    borderRadius={4}
                  >
                    <DateTimeSection
                      title={t("dateTimeSections.Local Auction Starts At")}
                      disabled={selectedCar?.Live_Auction_DateTime}
                      formik={formik}
                      dateField="localAuctionDate"
                      timeField="localAuctionTime"
                      showNextAvailable
                      isLocalAuction
                    />
                  </Grid>
                )}
                {formik.values.localAuctionBuyNow && (
                  <Grid
                    item
                    size={{ xs: 12, lg: 6 }}
                    border={"2px dashed #E0E0E0"}
                    borderColor={"border.main"}
                    borderRadius={4}
                  >
                    <DateTimeSection
                      title={t("dateTimeSections.Buy Now Ends At")}
                      disabled={selectedCar?.Buy_now_DateTime}
                      formik={formik}
                      dateField="localBuyNowDate"
                      timeField="localBuyNowTime"
                    />
                  </Grid>
                )}
              </Grid>
            )}

            {formik.values.exportAuction && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {formik.values.exportAuctionTimed && (
                  <Grid
                    item
                    size={{ xs: 12, lg: 6 }}
                    border={"2px dashed #E0E0E0"}
                    borderColor={"border.main"}
                    borderRadius={4}
                  >
                    <DateTimeSection
                      title={t("dateTimeSections.Export Auction Starts At")}
                      disabled={selectedCar?.Live_Auction_DateTime}
                      formik={formik}
                      dateField="exportAuctionDate"
                      timeField="exportAuctionTime"
                      showNextAvailable
                    />
                  </Grid>
                )}
                {formik.values.exportAuctionBuyNow && (
                  <Grid
                    item
                    size={{ xs: 12, lg: 6 }}
                    border={"2px dashed #E0E0E0"}
                    borderColor={"border.main"}
                    borderRadius={4}
                  >
                    <DateTimeSection
                      title={t("dateTimeSections.Buy Now Ends At")}
                      disabled={selectedCar?.Buy_now_DateTime}
                      formik={formik}
                      dateField="exportBuyNowDate"
                      timeField="exportBuyNowTime"
                    />
                  </Grid>
                )}
              </Grid>
            )}

            <Grid container spacing={2}>
              <DeliveryOptionsSection formik={formik} />
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                sx={{ px: 4 }}
                disabled={isLoading}
                onClick={formik.handleSubmit}
              >
                {isLoading ? (
                  <CircularProgress size={22} />
                ) : (
                  t("buttons.Save And List My Car Now")
                )}
              </Button>
            </Box>
          </form>
          <Modal
            open={openSellMyCarAsap}
            onClose={() => setOpenSellMyCarAsap(false)}
            aria-labelledby="car-added-to-sale-title"
            aria-describedby="car-added-to-sale-description"
          >
            {getVehicleDetailsApiData?.data?.vehicle?.car_label_status === CAR_STATUS.RELIST_PENDING ? (
              <CarRelistedModal
                handleClose={() => {
                  setOpenSellMyCarAsap(false);
                  navigate(`${routes.dashboardNavigation}${routes.myGarage}`);
                }}
              />
            ) : (
              <CarAddedToSaleModal
                handleClose={() => {
                  setOpenSellMyCarAsap(false);
                  navigate(`${routes.dashboardNavigation}${routes.myGarage}`);
                }}
              />
            )}
            
          </Modal>

          {openEditSuccessMessageModal ? <Modal
            open={openEditSuccessMessageModal}
            onClose={() => setOpenEditSuccessMessageModal(false)}
          >
            <AuctionDateTimeEditedMessageModal
              handleClose={() => {
                setOpenEditSuccessMessageModal(false);
                navigate(`${routes.dashboardNavigation}${routes.myGarage}`);
              }}
            />
          </Modal> : null}
        </Paper>
      {/* </DashboardMainContainer> */}

      {getVehicleOfferPriceApiData?.isFetching ? (
        <Modal
          open={getVehicleOfferPriceApiData?.isFetching}
          sx={{
            background: getRGBAFromColor({
              color: theme.palette.text.primary,
              alpha: "0.45"
            })
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            <CircularProgress 
              size={50}
            />
          </Box>
        </Modal>
      ) : null}

      {editAuctionDateAndTime.open ? (
        <Modal
          open={editAuctionDateAndTime.open}
          sx={{
            background: getRGBAFromColor({
              color: theme.palette.text.primary,
              alpha: "0.45"
            }),
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          onClose={() => {
            setEditAuctionDateAndTime({...editAuctionDateAndTimeInit});
          }}
        >
          <Box
            sx={{
              width: "fit-content",
              // height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              background: theme.palette.background.default,
              borderRadius: "1rem",
              paddingBottom: "1rem"
            }}
          >
            {editAuctionDateAndTime.auction_type === auctionTypes.Live_Auction_DateTime && editAuctionDateAndTime.market_type === localAuction ? <DateTimeSection
              title={t("dateTimeSections.Local Auction Starts At")}
              formik={formik}
              dateField="localAuctionDate"
              timeField="localAuctionTime"
              showNextAvailable
              isLocalAuction
            /> : null}

            {editAuctionDateAndTime.auction_type === auctionTypes.Buy_now_DateTime && editAuctionDateAndTime.market_type === localAuction ? (
              <DateTimeSection
                title={t("dateTimeSections.Buy Now Ends At")}
                formik={formik}
                dateField="localBuyNowDate"
                timeField="localBuyNowTime"
              />
            ) : null} 

            {editAuctionDateAndTime.auction_type === auctionTypes.Live_Auction_DateTime && editAuctionDateAndTime.market_type === exportAuction ? <DateTimeSection
              title={t("dateTimeSections.Export Auction Starts At")}
              formik={formik}
              dateField="exportAuctionDate"
              timeField="exportAuctionTime"
              showNextAvailable
            /> : null}

            {editAuctionDateAndTime.auction_type === auctionTypes.Buy_now_DateTime && editAuctionDateAndTime.market_type === exportAuction ? <DateTimeSection
              title={t("dateTimeSections.Buy Now Ends At")}
              formik={formik}
              dateField="exportBuyNowDate"
              timeField="exportBuyNowTime"
            /> : null}

            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{ px: 4 }}
              disabled={isLoading}
              onClick={formik.handleSubmit}
            >
              {isLoading ? (
                <CircularProgress size={22} />
              ) : (
                "Edit auction date and time"
              )}
            </Button>
          </Box>
        </Modal>
      ) : null}
    </Box>
  );
};
